import { logger } from '@/common/api/logger';
import { parseErrorMessage } from '@/common/error';
import { MaintenanceWindowInput } from './Maintenance.types';
import { MaintenanceWindowService } from './MaintenanceWindow.service';
import { ServerDateService } from './ServerDate/ServerDate.service';

function createMaintenanceService(windows: MaintenanceWindowInput[]) {
  const serverDateService = new ServerDateService();
  const maintenanceWindowService = new MaintenanceWindowService(serverDateService, windows);

  return maintenanceWindowService.initialize();
}

let singletonService: MaintenanceWindowService | null = null;
let singletonInitializationPromise: Promise<MaintenanceWindowService> | null = null;

export async function setupMaintenanceLib(windows: MaintenanceWindowInput[]) {
  if (singletonService) {
    return singletonService;
  }
  if (!singletonInitializationPromise) {
    singletonInitializationPromise = createMaintenanceService(windows);
    singletonInitializationPromise.finally(() => {
      singletonInitializationPromise = null;
    });
  }
  singletonService = await singletonInitializationPromise;
  return singletonService;
}

export async function getMaintenanceServiceAsync(): Promise<MaintenanceWindowService> {
  if (!singletonInitializationPromise && !singletonService) {
    throw new Error('Maintenance service has not been initialized. Please call setupMaintenanceLib first.');
  }
  if (singletonService) {
    return singletonService;
  }
  if (singletonInitializationPromise) {
    await singletonInitializationPromise;
  }
  return singletonService!;
}

/**
 * Check if maintenance is ongoing without guaranteeing the accuracy of the result.
 * This is useful for features that want to disable themselves during maintenance,
 * but don't need to worry about setting up the windows.
 *
 * @returns check if maintenance is ongoing.
 */
export async function looselyCheckIfMaintenanceIsOngoing(): Promise<boolean> {
  try {
    const maintenanceService = await getMaintenanceServiceAsync();
    return maintenanceService.getCurrentStatus().phase === 'ongoing';
  } catch (error) {
    logger.error(
      'Maintenance service is not available, unable to determine maintenance status. Assuming not in maintenance.',
      { error: parseErrorMessage(error) }
    );
    return false;
  }
}
